import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { fromMinorUnits } from '@/api/map/agent'
import { fromMinorUnitsSigned } from '@/api/map/pnl'
import type { PnlQuery } from '@/data/api'
import * as api from '@/data/api'
import { ROLES, type GameBucket, type PlTypeName, type PnlRow, type Role, type Sport } from '@/data/types'

/** Agent-API analytics `GET /v1/agents/{id}/pl-statement` split cell. */
export type AnalyticsPlSplitCell = {
  name?: string
  netMinor: string
}

export type AnalyticsPlStatementRow = {
  id: string
  settledAt: string
  memberId: string
  name: string
  parentId?: string
  sport: string
  game: string
  marketId: string
  eventId: string
  eventName: string
  marketName: string
  turnoverMinor: string
  memberNetMinor: string
  bookNetMinor: string
  commissionMinor?: string
  betCount: number
  split?: Record<string, AnalyticsPlSplitCell>
}

export type AnalyticsPlStatementPage = {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  page?: number
  size?: number
  total?: number
  netMinor?: string
  splitRoles?: string[]
  rows: AnalyticsPlStatementRow[]
}

export type PlStatementQuery = PnlQuery & {
  q?: string
  /** Company Master Triple Master filter — Agent-API `tms`. */
  tms?: string
  /** Hierarchy drill-down — Agent-API `name: { _id, accessLevel }`. */
  name?: { _id: string; accessLevel?: string }
  /** Lab `Game/getGames` `_id`, or omit for all products. */
  game?: string
  /** Lab `Account/getSubGames` name, or omit for every sub-game. */
  subGame?: string
  /** Lab `Account/getEventsForPLL` event name, or omit for every event. */
  eventName?: string
  /** Lab Select Type → `typeName` (Match Odds / Bookmaker / Session / …). */
  typeName?: PlTypeName
  /** Lab staff Market access — rewrites typeName server-side when set. */
  bookmakerSessionFlag?: 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'
}

export type PlStatementRowView = Omit<PnlRow, 'marketId' | 'sport'> & {
  marketId: string
  /** Lab `gameData.subGame` (Cricket, Live ROULETTE, teenpatti, …). */
  sport: string
  memberName: string
  splitNames: Partial<Record<Role, string>>
  /** Lab Invalid Bets — Event chips (not OR’d into isPNL*). */
  isInvalid?: boolean
  isRollback?: boolean
  /** Lab Invalidate P&L flags — list still shows these rows. */
  isPNLInvalid?: boolean
  isPNLRollBack?: boolean
  previousBalance?: number
  nextBalance?: number
  exposure?: number
}

export type PlStatementResult = {
  rows: PlStatementRowView[]
  /** Full filtered-set ladder columns, stable across server pages. */
  splitRoles: Role[]
  total: number
  page: number
  pageSize: number
  netPnl: number
  asOf?: string
  martStale?: boolean
  unavailable?: { code: string; reason: string; trackedBy?: string }
}

const SPORTS: readonly Sport[] = [
  'Cricket',
  'Soccer',
  'Tennis',
  'Horse Racing',
  'Greyhound Racing',
  'Casino',
]

const GAMES: readonly GameBucket[] = [
  'SportsBook',
  'SportsBook - Bookmaker',
  'Fancy',
  'Racing',
  'Live Casino',
  'Crash Game',
  'Card Game',
  'Credit Limit',
]

const ROLE_SET = new Set<string>(ROLES)

export function asSport(raw: string): Sport {
  return SPORTS.includes(raw as Sport) ? (raw as Sport) : 'Cricket'
}

export function asGame(raw: string): GameBucket {
  return GAMES.includes(raw as GameBucket) ? (raw as GameBucket) : 'SportsBook'
}

/** Lab Account Statement transfer / wallet subGames (not Sport enums). */
const STATEMENT_TRANSFER_SUBGAMES = new Set([
  'downlineTransfer',
  'uplineTransfer',
  'Re-Deposit',
  'Deposit',
  'Withdrawal',
])

export function isStatementTransferSubGame(subGame: string | null | undefined): boolean {
  return STATEMENT_TRANSFER_SUBGAMES.has(String(subGame ?? '').trim())
}

/**
 * Lab PL “SubGame” cell. Live Casino / Card Game must not run through
 * `asSport(… || 'Cricket')` — Lab Live ROULETTE (and SoftGamings empty stamps)
 * are not Sport enums, so they were painted as Cricket.
 * Give/Take transfer labels must stay literal (Details column + Description).
 */
export function plStatementSubGame(
  game: GameBucket,
  rawSubGame: string | null | undefined,
): string {
  const raw = String(rawSubGame ?? '').trim()
  if (game === 'Live Casino' || game === 'Crash Game') {
    // Sportsbook sport stamps on casino settles are wrong; treat as missing.
    if (
      !raw ||
      raw === 'None' ||
      raw === 'Cricket' ||
      raw === 'Soccer' ||
      raw === 'Tennis'
    ) {
      return 'Casino'
    }
    return raw
  }
  if (game === 'Card Game') {
    return raw && raw !== 'None' ? raw : 'Card Game'
  }
  if (game === 'Racing') {
    return raw && raw !== 'None' ? raw : 'Horse Racing'
  }
  if (STATEMENT_TRANSFER_SUBGAMES.has(raw)) return raw
  return asSport(raw || 'Cricket')
}

export function asRole(raw: string): Role | null {
  return ROLE_SET.has(raw) ? (raw as Role) : null
}

export function emptyPlStatement(query: PlStatementQuery): PlStatementResult {
  const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE
  return {
    rows: [],
    splitRoles: [],
    total: 0,
    page: query.page ?? 1,
    pageSize,
    netPnl: 0,
  }
}

export function unavailablePlStatement(
  query: PlStatementQuery,
  reason?: string,
): PlStatementResult {
  return {
    ...emptyPlStatement(query),
    unavailable: {
      code: 'no_pl_statement_source',
      reason:
        reason ??
        'Event-level P&L needs Agent-API hierarchy POST /v1/account/getDownlineAccountsData. It is not reachable, so this console will not invent settled rows.',
      trackedBy: 'backend-ui-reports:pl-statement',
    },
  }
}

/**
 * Lab PL footer: today window (Mongo/PG) or historical BQ ≤93 days
 * (`PLStatementCtrl` + `getTotalNetAmountNew` / SumOnly).
 */
export function canLoadSumOnly(range: { from: string; to: string }, _now = new Date()): boolean {
  const from = Date.parse(`${range.from.slice(0, 10)}T00:00:00Z`)
  const to = Date.parse(`${range.to.slice(0, 10)}T00:00:00Z`)
  const day = 24 * 60 * 60 * 1000
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return false
  const diffDays = Math.ceil(Math.abs(to - from) / day)
  return diffDays <= 93
}

/** Lab toastr when range span exceeds 93 days. */
export function plStatementRangeError(range: { from: string; to: string }): string | null {
  const from = Date.parse(`${range.from.slice(0, 10)}T00:00:00Z`)
  const to = Date.parse(`${range.to.slice(0, 10)}T00:00:00Z`)
  const day = 24 * 60 * 60 * 1000
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return null
  const diffDays = Math.ceil(Math.abs(to - from) / day)
  if (diffDays > 93) {
    return 'The date you have selected exceeds 93 days. Please try again.'
  }
  return null
}

function splitFromAnalytics(
  split: AnalyticsPlStatementRow['split'],
): { amounts: PnlRow['split']; names: Partial<Record<Role, string>> } {
  const amounts: PnlRow['split'] = {}
  const names: Partial<Record<Role, string>> = {}
  if (!split) return { amounts, names }
  for (const [key, cell] of Object.entries(split)) {
    const role = asRole(key)
    if (!role || !cell) continue
    amounts[role] = fromMinorUnitsSigned(cell.netMinor)
    if (cell.name) names[role] = cell.name
  }
  return { amounts, names }
}

export function toPlStatementRow(row: AnalyticsPlStatementRow): PlStatementRowView {
  const memberPnl = fromMinorUnitsSigned(row.memberNetMinor)
  const bookNet = fromMinorUnitsSigned(row.bookNetMinor)
  const commission = row.commissionMinor ? fromMinorUnits(row.commissionMinor) : 0
  const { amounts, names } = splitFromAnalytics(row.split)
  const game = asGame(row.game)
  return {
    id: row.id,
    settledAt: row.settledAt,
    memberId: row.memberId,
    sport: plStatementSubGame(game, row.sport),
    game,
    marketId: row.marketId,
    eventId: row.eventId,
    eventName: row.eventName,
    marketName: row.marketName,
    memberPnl,
    commission,
    netAmount: -bookNet,
    split: amounts,
    betCount: row.betCount ?? 1,
    turnover: fromMinorUnits(row.turnoverMinor),
    memberName: row.name,
    splitNames: names,
  }
}

export function toPlStatementResult(
  live: AnalyticsPlStatementPage,
  query: PlStatementQuery,
): PlStatementResult {
  const pageSize = live.size ?? query.pageSize ?? DEFAULT_PAGE_SIZE
  const apiPage = live.page ?? 0
  const rows = (live.rows ?? []).map(toPlStatementRow)
  const splitRoles = (live.splitRoles ?? [])
    .map(asRole)
    .filter((role): role is Role => role !== null)
  return {
    rows,
    splitRoles:
      splitRoles.length > 0
        ? splitRoles
        : ROLES.filter((role) => rows.some((row) => typeof row.split[role] === 'number')),
    total: live.total ?? live.rows?.length ?? 0,
    page: apiPage + 1,
    pageSize,
    netPnl: fromMinorUnitsSigned(live.netMinor),
    asOf: live.asOf,
    martStale: live.martStale,
  }
}

export function fromFixturePnl(
  summary: api.PnlSummary,
  query: PlStatementQuery,
  memberName: (id: string) => string,
): PlStatementResult {
  const needle = query.q?.trim().toLowerCase() ?? ''
  const filtered = summary.rows.filter((row) => {
    if (query.subGame && row.sport !== query.subGame) return false
    if (query.eventName && row.eventName !== query.eventName) return false
    if (!needle) return true
    return (
      row.eventName.toLowerCase().includes(needle) ||
      row.marketName.toLowerCase().includes(needle) ||
      memberName(row.memberId).toLowerCase().includes(needle)
    )
  })

  const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE
  const total = filtered.length
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1)
  const page = Math.min(Math.max(1, query.page ?? 1), pageCount)
  const start = (page - 1) * pageSize
  const rows: PlStatementRowView[] = filtered.slice(start, start + pageSize).map((row) => ({
    ...row,
    marketId: row.marketId ?? row.eventId,
    memberName: memberName(row.memberId),
    splitNames: {},
  }))

  const netPnl = needle || query.subGame || query.eventName
    ? filtered.reduce((sum, row) => sum - row.netAmount, 0)
    : summary.totals.netPnl

  return {
    rows,
    splitRoles: ROLES.filter((role) =>
      filtered.some((row) => typeof row.split[role] === 'number'),
    ),
    total,
    page,
    pageSize,
    netPnl,
    asOf: summary.asOf,
    martStale: summary.martStale,
  }
}

/** Lab `mapAccountsAPI` party — amounts are already major units. */
export type DownlineAccountParty = {
  _id?: string
  name: string
  isVirtual?: boolean
  userAccessLevel?: string
  amount: { winAmount?: number; loseAmount?: number; netAmount: number }
  commission?: { turnOver?: number; netCommission?: number }
}

export type DownlineAccountRow = {
  _id: string
  createdAt: string
  /** Settlement currency from Agent-API (INR / POINTS / POINT@1). */
  currencyType?: string
  /** Lab Invalid Bets — Event chips. */
  isInvalid?: boolean
  isRollback?: boolean
  /** Lab Invalidate P&L flags — list still shows these rows. */
  isPNLInvalid?: boolean
  isPNLRollBack?: boolean
  gameData: {
    eventName?: string
    gameName?: string
    matchName?: string
    subGame?: string
    betCount?: number
    isBookmaker?: boolean
    isFancy?: boolean
    isCardGame?: boolean
    marketId?: string
    eventId?: string
  }
  playerData: DownlineAccountParty
  masterData?: DownlineAccountParty
  superMasterData?: DownlineAccountParty
  doubleMasterData?: DownlineAccountParty
  tripleMasterData?: DownlineAccountParty
  companyMasterData?: DownlineAccountParty
  previousBalance?: number
  nextBalance?: number
  exposure?: number
  currBalance?: number
}

export type GetDownlineAccountsDataResponse = {
  value: boolean
  data?: {
    maxRow?: number
    result?: DownlineAccountRow[]
    totalCount?: number
    net?: number
  }
}

export type GetDownlineAccountsSumOnlyResponse = {
  value: boolean
  data?: {
    masterNetAmountSum?: number
    superMasterNetAmountSum?: number
    doubleMasterNetAmountSum?: number
    tripleMasterNetAmountSum?: number
    companyMasterNetAmountSum?: number
    totalRecords?: number
  }
}

/** Only the signed-in caller's role seat is non-zero in the Strix response. */
export function netFromDownlineSumOnly(
  response: GetDownlineAccountsSumOnlyResponse | null | undefined,
): number | undefined {
  if (!response?.value || !response.data) return undefined
  const values = [
    response.data.masterNetAmountSum,
    response.data.superMasterNetAmountSum,
    response.data.doubleMasterNetAmountSum,
    response.data.tripleMasterNetAmountSum,
    response.data.companyMasterNetAmountSum,
  ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  return values.length ? values.reduce((sum, value) => sum + value, 0) : undefined
}

const LAB_PARTY: { key: keyof DownlineAccountRow; role: Role }[] = [
  { key: 'masterData', role: 'master' },
  { key: 'superMasterData', role: 'super-master' },
  { key: 'doubleMasterData', role: 'double-master' },
  { key: 'tripleMasterData', role: 'triple-master' },
  { key: 'companyMasterData', role: 'company-master' },
]

const LAB_ACCESS: Record<Role, string> = {
  'company-master': 'companyMaster',
  'triple-master': 'tripleMaster',
  'double-master': 'doubleMaster',
  'super-master': 'superMaster',
  master: 'master',
  player: 'player',
}

export function labAccessLevel(role: Role): string {
  return LAB_ACCESS[role]
}

function gameFromDownline(row: DownlineAccountRow): GameBucket {
  const g = row.gameData
  if (g.isCardGame) return 'Card Game'
  if (g.isFancy) return 'Fancy'
  if (g.isBookmaker) return 'SportsBook - Bookmaker'
  return asGame(g.gameName ?? 'SportsBook')
}

export function toPlStatementRowFromDownline(row: DownlineAccountRow): PlStatementRowView {
  const memberPnl = row.playerData.amount.netAmount
  const split: PnlRow['split'] = {}
  const splitNames: Partial<Record<Role, string>> = {}
  for (const { key, role } of LAB_PARTY) {
    const party = row[key]
    if (!party || typeof party !== 'object' || !('amount' in party)) continue
    split[role] = party.amount.netAmount
    if (party.name && party.name !== 'NA') splitNames[role] = party.name
  }
  const marketId = row.gameData.marketId?.trim() || row._id
  const currencyType = row.currencyType?.trim() || undefined
  const game = gameFromDownline(row)
  return {
    id: row._id,
    settledAt: row.createdAt,
    memberId: row.playerData._id ?? '',
    sport: plStatementSubGame(game, row.gameData.subGame),
    game,
    marketId,
    eventId: row.gameData.eventId?.trim() || marketId,
    eventName: row.gameData.eventName ?? '',
    marketName: row.gameData.matchName ?? '',
    memberPnl,
    commission: row.playerData.commission?.netCommission ?? 0,
    netAmount: memberPnl,
    split,
    betCount: row.gameData.betCount ?? 1,
    turnover: row.playerData.commission?.turnOver ?? 0,
    currencyType,
    memberName: row.playerData.name,
    splitNames,
    isPNLInvalid: Boolean(row.isPNLInvalid),
    isPNLRollBack: Boolean(row.isPNLRollBack),
    isInvalid: Boolean(row.isInvalid),
    isRollback: Boolean(row.isRollback),
    ...(row.previousBalance != null ? { previousBalance: row.previousBalance } : {}),
    ...(row.nextBalance != null || row.currBalance != null
      ? { nextBalance: row.nextBalance ?? row.currBalance }
      : {}),
    ...(row.exposure != null ? { exposure: row.exposure } : {}),
  }
}

export function fromDownlineAccountsData(
  live: GetDownlineAccountsDataResponse,
  query: PlStatementQuery,
  sumOnly?: GetDownlineAccountsSumOnlyResponse | null,
): PlStatementResult {
  const pageSize = live.data?.maxRow ?? query.pageSize ?? DEFAULT_PAGE_SIZE
  if (!live.value) return emptyPlStatement({ ...query, pageSize })
  const raw = live.data?.result ?? []
  const rows = raw.map(toPlStatementRowFromDownline)
  const splitRoles = LAB_PARTY.filter(({ key }) => raw.some((row) => Boolean(row[key]))).map(
    ({ role }) => role,
  )
  const pageNet = rows.reduce((sum, row) => sum - row.memberPnl, 0)
  const roleNet = netFromDownlineSumOnly(sumOnly)
  const sumTotal = sumOnly?.value ? sumOnly.data?.totalRecords : undefined
  return {
    rows,
    splitRoles,
    total:
      typeof sumTotal === 'number' && sumTotal > 0
        ? sumTotal
        : (live.data?.totalCount ?? raw.length),
    page: query.page ?? 1,
    pageSize,
    // Prefer SumOnly role net when the caller passed it; otherwise Agent-API
    // `data.net` (full filtered set) then this page’s inverted member P&L.
    netPnl: roleNet ?? (typeof live.data?.net === 'number' ? live.data.net : pageNet),
  }
}

export type GetEventsForPllResponse = {
  value: boolean
  data: string[]
}

/** Lab callback `{ value, data: string[] }` — names for the Event picker. */
export function namesFromGetEventsForPll(response: GetEventsForPllResponse): string[] {
  if (!response.value) return []
  return (response.data ?? []).filter(
    (name): name is string => typeof name === 'string' && Boolean(name.trim()),
  )
}

/** Demo / fixture event names — same strings as Agent-API mock-hierarchy. */
export const FIXTURE_PLL_EVENTS = ['Ind vs Aus', 'Wimbledon'] as const

export function fixtureEventsForPll(
  rows: Array<{ eventName: string; sport: string }>,
  query: Pick<PlStatementQuery, 'subGame'>,
): string[] {
  const names = new Set<string>()
  for (const row of rows) {
    if (query.subGame && row.sport !== query.subGame) continue
    if (row.eventName) names.add(row.eventName)
  }
  if (names.size === 0) return [...FIXTURE_PLL_EVENTS]
  return [...names].sort((a, b) => a.localeCompare(b))
}
